import { useSearchParams } from 'react-router-dom';
import { APP_NAME } from '@worktracker/shared';
import { getSsoLoginUrl } from '@/lib/sso';

const ERROR_MESSAGES: Record<string, string> = {
  forbidden:
    'You do not have permission to access this application. Only WiLLDesignTech org members can sign in.',
  auth_failed: 'Authentication failed. Please try again.',
  session_expired: 'Your session has expired. Please sign in again.',
};

export function LoginPage() {
  const [params] = useSearchParams();
  const error = params.get('error');

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0f1117]">
      {/* Animated gradient background */}
      <div className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-[-25%] h-[150%] w-[150%] animate-[drift_20s_ease-in-out_infinite_alternate]"
          style={{
            background: `
              radial-gradient(ellipse at 30% 20%, rgba(30, 136, 229, 0.12) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 80%, rgba(64, 222, 197, 0.10) 0%, transparent 50%),
              radial-gradient(ellipse at 50% 50%, rgba(88, 194, 217, 0.06) 0%, transparent 60%)
            `,
          }}
        />
      </div>

      {/* Grid pattern */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(88, 194, 217, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(88, 194, 217, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10 w-full max-w-[420px] px-6">
        <div
          className="rounded-2xl border border-[rgba(88,194,217,0.12)] p-10"
          style={{
            background: 'rgba(22, 25, 35, 0.85)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 48px rgba(0,0,0,0.3), 0 0 0 1px rgba(88,194,217,0.05) inset',
          }}
        >
          {/* WD Logo */}
          <div className="mx-auto mb-8 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gradient">
            <span className="text-lg font-bold text-white">WD</span>
          </div>

          <h1 className="mb-2 text-center text-[22px] font-bold tracking-tight text-[#f0f2f5]">
            {APP_NAME}
          </h1>
          <p className="mb-9 text-center text-sm text-[#8b929e]">
            Sign in to view daily standup reports
          </p>

          {error && (
            <div className="mb-6 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
              {ERROR_MESSAGES[error] ?? 'An unknown error occurred.'}
            </div>
          )}

          <a
            href={getSsoLoginUrl()}
            className="group flex w-full items-center justify-center gap-2.5 rounded-[10px] px-6 py-3.5 text-[15px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(30,136,229,0.35)]"
            style={{ background: 'linear-gradient(135deg, #1E88E5, #4DC9C5)' }}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            Sign in with WillDesign SSO
          </a>

          <p className="mt-7 text-center text-xs text-[#555c68]">
            You need to be a member of the
            <br />
            <span className="font-medium text-[#6e7681]">WiLLDesignTech</span> organization
          </p>
        </div>
      </div>
    </div>
  );
}
